import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AvisoDTO } from '../../../models/dashboard/aviso.model';
import { AvisosService } from '../../../services/dashboard/avisos.service';
import { environment } from '../../../environments/environment';

/**
 * Intervalo total (ms) entre avancos automaticos de pagina do carrossel.
 * 10s alinha-se ao ritmo de leitura confortavel para os cards.
 */
const INTERVALO_AUTOPLAY_MS = 10000;

/**
 * Periodo (ms) de cada "tick" do timer de progresso. 100ms da uma animacao
 * suave da barra (10 atualizacoes por segundo) sem custo perceptivel.
 */
const TICK_MS = 100;

/**
 * Largura limite (px) abaixo da qual o p-carousel troca para numVisible=1
 * (alinhado com `responsiveOptions.breakpoint = 1024px`). Usado em leitura
 * sob demanda dentro de `avancarPagina()` para calcular a ultima pagina
 * sem precisar de listener de resize.
 */
const BREAKPOINT_MOBILE_PX = 1024;

@Component({
  selector: 'app-dashboard-avisos',
  templateUrl: './dashboard-avisos.component.html',
  styleUrls: ['./dashboard-avisos.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardAvisosComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  @Input() avisos: AvisoDTO[] = [];

  /**
   * Lista usada como `[value]` do `<p-carousel>`. Recebe um `null` prepended
   * a esquerda E outro `null` appended a direita da lista original. Assim,
   * com 3 itens visiveis e `circular=false`, tanto o primeiro quanto o
   * ULTIMO aviso podem ser exibidos no slot central:
   *
   * Exemplo com [A, B, C, D]:
   *   - avisosRotacionados = [null, A, B, C, D, null]
   *   - Pagina 0 (3 visiveis): [null, A, B]   -> A no centro (destacado).
   *   - Pagina 1:               [A, B, C]      -> B no centro.
   *   - Pagina 2:               [B, C, D]      -> C no centro.
   *   - Pagina 3:               [C, D, null]   -> D no centro (ultimo destacavel).
   *
   * O slot vazio extra a direita corrige o sintoma anterior em que o
   * ultimo card ficava preso a direita sem possibilidade de centralizar.
   *
   * Casos de borda:
   *   - avisos null/undefined ou vazio -> [].
   *   - avisos.length >= 1             -> [null, ...avisos, null].
   *
   * Tipo permite `null` para representar os slots vazios; o template trata
   * esse caso com *ngIf antes de renderizar o card.
   */
  avisosRotacionados: (AvisoDTO | null)[] = [];

  showDialog = false;
  textoDialogo = '';

  /**
   * Conjunto de ids de avisos cuja imagem do banner falhou ao carregar.
   * Usado para alternar para o placeholder visual no template,
   * evitando que o texto do `alt` vaze sobre o card quando a URL retorna 404.
   */
  imagensFalhadas = new Set<number>();

  /**
   * Configuracao responsiva do p-carousel.
   *
   * - Em telas largas (default), o carrossel mostra 3 itens visiveis para
   *   produzir o efeito "peek": card central destacado e cards laterais
   *   reduzidos (efeito controlado via SCSS + classe `aviso-card--destaque`
   *   aplicada via JS no item do meio entre os ativos).
   * - Em telas menores que 1024px, voltamos ao comportamento original
   *   (1 card centralizado, sem peek), pois nao ha espaco horizontal
   *   suficiente para mostrar 3 cards lado a lado.
   */
  responsiveOptions = [
    { breakpoint: '1024px', numVisible: 1, numScroll: 1 }
  ];

  // -------------------------------------------------------------------------
  // Autoplay com indicador visual de progresso.
  //
  // Como funciona:
  //   - Um `setInterval` dispara `tick()` a cada `TICK_MS`.
  //   - `progresso` (0..100) avanca proporcionalmente a `INTERVALO_AUTOPLAY_MS`.
  //   - Ao atingir 100%, reseta para 0 e avanca `paginaAtual`.
  //   - Quando `pausado=true` (mouse sobre o carrossel) o tick simplesmente
  //     ignora o incremento, deixando a barra congelada na posicao atual.
  //   - O loop manual (paginaAtual -> 0) e feito em `avancarPagina`, ja que
  //     `[circular]="false"` no PrimeNG.
  // -------------------------------------------------------------------------

  /** Progresso atual do timer (0..100). Vincula-se a largura da barra visual. */
  progresso = 0;

  /** Indica se o autoplay esta pausado (mouse sobre o carrossel). */
  pausado = false;

  /** Pagina atualmente exibida no `<p-carousel>` (two-way binding via [(page)]). */
  paginaAtual = 0;

  /** Handle do `setInterval` do timer; null quando parado. */
  private intervaloProgresso: ReturnType<typeof setInterval> | null = null;

  private langChangeSub!: Subscription;

  // -------------------------------------------------------------------------
  // Loop manual nas setas do carrossel.
  //
  // Como `[circular]="false"` no `<p-carousel>` (necessario porque
  // `circular=true` produz bugs conhecidos com clones de itens), o PrimeNG
  // desabilita os botoes `.p-carousel-prev` na primeira pagina e
  // `.p-carousel-next` na ultima. O usuario fica preso no fim do carrossel
  // e nao consegue voltar ao primeiro card via clique direto na seta.
  //
  // Estrategia:
  //   1. MutationObserver: vigia o `.p-carousel` para detectar quando o
  //      PrimeNG (re)aplica `disabled` / classe `.p-disabled` nos botoes.
  //      Quando aplica, removemos para garantir que o botao continue
  //      clicavel mesmo no limite -- assim o evento `click` chega ate o
  //      nosso listener.
  //   2. Listener `click` em fase de captura no wrapper `.p-carousel`:
  //      detecta clique nas setas (via `closest()`) e, se for clique em
  //      `next` na ultima pagina ou `prev` na primeira, intercepta o
  //      evento (preventDefault + stopImmediatePropagation) e aplica o
  //      loop manual (paginaAtual = 0 ou ultimaPagina).
  //
  // Limpamos observer e listener em `ngOnDestroy` para evitar vazamentos.
  // -------------------------------------------------------------------------

  /** Observer para reabilitar continuamente os botoes do carrossel. */
  private observadorNavegadores: MutationObserver | null = null;

  /** Funcao para remover o listener de clique em capture phase. */
  private removerListenerClique: (() => void) | null = null;

  constructor(
    private translate: TranslateService,
    private avisosService: AvisosService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    // Garante que a lista rotacionada esteja consistente mesmo quando o
    // input chega antes do primeiro ngOnChanges (cenario raro, mas
    // defensivo).
    this.atualizarAvisosRotacionados();

    // Mantemos a inscricao para forcar repintura com OnPush quando o idioma muda,
    // ja que o pipe translate atualiza automaticamente mas o p-carousel as vezes
    // nao recompoe os itens.
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.cdr.markForCheck();
      // Apos o reidioma o carrossel pode regenerar items; reaplica destaque.
      this.agendarAtualizacaoDestaque();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Sempre que o input `avisos` muda recalculamos a lista rotacionada,
    // pois a logica de destaque do carrossel depende da ordem dos itens.
    if (changes['avisos']) {
      this.atualizarAvisosRotacionados();
      // Apos trocar o `[value]` do carrossel o PrimeNG remonta os itens;
      // reaplica o destaque no proximo tick.
      this.agendarAtualizacaoDestaque();

      // Quando a lista de avisos muda (e ha mais de 1 item), reposiciona
      // o carrossel no inicio e reinicia o timer para um ciclo limpo.
      if (this.avisos && this.avisos.length > 1) {
        this.paginaAtual = 0;
        this.progresso = 0;
        this.iniciarTimer();
      } else {
        // 0 ou 1 aviso: nao faz sentido autoplay; apenas para o timer.
        this.pararTimer();
        this.progresso = 0;
      }
    }
  }

  ngAfterViewInit(): void {
    // Aguarda o p-carousel renderizar os itens no DOM antes de aplicar o
    // destaque no item central. Sem o setTimeout o querySelectorAll
    // retorna lista vazia.
    this.agendarAtualizacaoDestaque();

    // Inicia o autoplay apenas apos a primeira renderizacao para evitar
    // que ticks anteriores ao bind do `[(page)]` se percam.
    this.iniciarTimer();

    // Configura o loop manual nas setas (intercepta clique em next/prev
    // no limite das paginas para reposicionar o carrossel).
    this.configurarLoopManualNavegadores();
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    this.pararTimer();

    // Limpa observer e listener das setas para evitar vazamentos.
    if (this.observadorNavegadores) {
      this.observadorNavegadores.disconnect();
      this.observadorNavegadores = null;
    }
    if (this.removerListenerClique) {
      this.removerListenerClique();
      this.removerListenerClique = null;
    }
  }

  /**
   * Monta a URL completa da imagem do banner do aviso.
   *
   * O backend retorna apenas o nome do arquivo em `AvisoDTO.imagemBanner`.
   * A URL final e montada como `{environment.imgHost}/aviso/{arquivo}`,
   * mesmo padrao usado em `pages/avisos/avisos.component.ts`.
   *
   * Mantem fallback defensivo para URLs absolutas (http/https) e
   * data URLs (base64), embora o cenario esperado seja apenas o nome.
   */
  getImagemBannerUrl(arquivo: string | null | undefined): string | null {
    if (!arquivo || String(arquivo).trim().length === 0) {
      return null;
    }
    const valor = String(arquivo).trim();
    if (valor.startsWith('http') || valor.startsWith('data:image')) {
      return valor;
    }
    return `${environment.imgHost}/aviso/${valor}`;
  }

  /**
   * Marca a imagem do aviso como falhada, fazendo o template exibir o
   * placeholder em vez da `<img>` quebrada.
   */
  onImagemErro(avisoId: number): void {
    if (!this.imagensFalhadas.has(avisoId)) {
      this.imagensFalhadas.add(avisoId);
      this.cdr.markForCheck();
    }
  }

  /**
   * Indica se a imagem do banner do aviso deve ser exibida (URL existe e
   * o load nao falhou previamente).
   */
  deveExibirImagem(aviso: AvisoDTO): boolean {
    const url = this.getImagemBannerUrl(aviso.imagemBanner);
    return !!url && !this.imagensFalhadas.has(aviso.id);
  }

  /**
   * Callback do evento `(onPage)` do `<p-carousel>`. Apos a troca de pagina
   * reposiciona o destaque visual no item central visivel e sincroniza
   * `paginaAtual` (cobre o caso do usuario navegar manualmente pelos
   * botoes/indicadores, garantindo que o autoplay continue do ponto certo).
   */
  onPaginaTrocada(event?: { page?: number }): void {
    if (event && typeof event.page === 'number' && event.page !== this.paginaAtual) {
      this.paginaAtual = event.page;
      // Interacao do usuario reinicia o ciclo da barra, evitando que o
      // proximo avanco automatico aconteca "em cima" da navegacao manual.
      this.progresso = 0;
    }
    this.agendarAtualizacaoDestaque();
  }

  /**
   * Pausa o autoplay quando o mouse entra no wrapper do carrossel.
   * A barra fica congelada na posicao atual (sem retroceder).
   */
  onMouseEnter(): void {
    if (!this.pausado) {
      this.pausado = true;
      this.cdr.markForCheck();
    }
  }

  /**
   * Retoma o autoplay quando o mouse sai do wrapper. O timer continua
   * exatamente de onde parou, pois o intervalo nao foi cancelado --
   * apenas os ticks ignoraram o incremento enquanto `pausado=true`.
   */
  onMouseLeave(): void {
    if (this.pausado) {
      this.pausado = false;
      this.cdr.markForCheck();
    }
  }

  onAvisoClicado(aviso: AvisoDTO): void {
    this.avisosService.registrarClique(aviso.id).subscribe();

    switch (aviso.tipoAviso) {
      case 'VIDEO':
        if (aviso.urlVideo) {
          window.open(aviso.urlVideo, '_blank');
        }
        break;
      case 'LINK':
        if (aviso.urlLink) {
          window.open(aviso.urlLink, '_blank');
        }
        break;
      case 'DIALOGO':
        if (aviso.textoDialogo) {
          this.textoDialogo = aviso.textoDialogo;
          this.showDialog = true;
        }
        break;
      case 'CHAT_WHATSAPP':
        if (aviso.numeroWhatsapp) {
          const texto = encodeURIComponent(aviso.textoChatWhatsapp || '');
          window.open(`https://wa.me/${aviso.numeroWhatsapp}?text=${texto}`, '_blank');
        }
        break;
      case 'PERFIL_INSTAGRAM':
        if (aviso.perfilInstagram) {
          window.open(`https://instagram.com/${aviso.perfilInstagram}`, '_blank');
        }
        break;
      case 'TUTORIAL':
      case 'NADA_A_FAZER':
        break;
    }
  }

  /**
   * Recalcula `avisosRotacionados` a partir do input `avisos`.
   *
   * Regra: prefixa um slot vazio (`null`) E sufixa outro slot vazio a lista
   * original. Assim, na pagina inicial do carrossel (3 itens visiveis) o
   * slot esquerdo aparece vazio e o aviso[0] fica no centro; e na pagina
   * final o slot direito aparece vazio e o ultimo aviso fica no centro.
   * Sem o sufixo o ultimo card ficava preso a direita, sem chance de ser
   * destacado.
   *
   * Casos de borda:
   *   - `avisos` null/undefined ou vazio -> [].
   *   - caso geral (1+ itens)            -> [null, ...avisos, null].
   *
   * Usamos apenas spread para nao mutar o array de entrada.
   */
  private atualizarAvisosRotacionados(): void {
    const origem = this.avisos;
    if (!origem || origem.length === 0) {
      this.avisosRotacionados = [];
      return;
    }
    // Slot vazio no inicio E no fim para permitir centralizar o primeiro
    // e o ultimo aviso sem usar [circular]="true" (que causou bugs antes).
    this.avisosRotacionados = [null, ...origem, null];
  }

  /**
   * Agenda a atualizacao do destaque para o proximo tick, dando tempo do
   * PrimeNG recompor os itens visiveis no DOM (especialmente apos onPage,
   * que dispara antes da animacao de transicao terminar).
   *
   * Usamos dois setTimeout em cascata: o primeiro espera o ciclo atual, o
   * segundo cobre o frame da transicao de pagina do PrimeNG.
   */
  private agendarAtualizacaoDestaque(): void {
    setTimeout(() => {
      this.atualizarDestaqueCentral();
      setTimeout(() => this.atualizarDestaqueCentral(), 50);
    }, 0);
  }

  /**
   * Aplica a classe `aviso-card--destaque` ao `.p-carousel-item` central
   * visivel e remove dos demais. Em telas estreitas (numVisible=1) marca
   * o unico item ativo; em telas largas (numVisible=3) marca o do meio.
   *
   * Por que JS e nao CSS puro: o PrimeNG 17 renderiza tambem itens nao
   * ativos como siblings, fazendo `:nth-of-type(2)` apontar para o item
   * errado e nao acompanhar as trocas de pagina. Selecionar pela classe
   * `.p-carousel-item-active` e pegar o indice 1 do array filtrado e
   * deterministico em qualquer pagina.
   */
  private atualizarDestaqueCentral(): void {
    const host = this.elementRef.nativeElement;
    const todos = host.querySelectorAll<HTMLElement>('.p-carousel-item');
    if (todos.length === 0) {
      return;
    }

    // Remove o destaque de todos.
    todos.forEach(el => this.renderer.removeClass(el, 'aviso-card--destaque'));

    // Considera ativos os itens com classe `.p-carousel-item-active`.
    const ativos: HTMLElement[] = [];
    todos.forEach(el => {
      if (el.classList.contains('p-carousel-item-active')) {
        ativos.push(el);
      }
    });

    if (ativos.length === 0) {
      return;
    }

    // O item "central" e o do meio dos ativos: indice floor(length/2).
    // - 3 ativos => indice 1 (o do meio, como pedido).
    // - 1 ativo  => indice 0 (mobile/sidebar estreita).
    // - 2 ativos => indice 1 (segundo visivel; cenario raro de borda).
    const indiceCentral = Math.floor(ativos.length / 2);
    const central = ativos[indiceCentral];
    if (central) {
      this.renderer.addClass(central, 'aviso-card--destaque');
    }
  }

  // -------------------------------------------------------------------------
  // Autoplay - controle do timer e avanco automatico de pagina.
  // -------------------------------------------------------------------------

  /**
   * (Re)inicia o timer de progresso. Sempre cancela um eventual intervalo
   * anterior antes de criar um novo para evitar timers duplicados quando
   * `avisos` muda em runtime (ex.: refresh do dashboard).
   *
   * Nao inicia o timer se houver 0 ou 1 aviso: nao faz sentido autoplay
   * pois nao existe proxima pagina para avancar.
   */
  private iniciarTimer(): void {
    this.pararTimer();
    if (!this.avisos || this.avisos.length <= 1) {
      return;
    }
    this.intervaloProgresso = setInterval(() => this.tick(), TICK_MS);
  }

  /**
   * Cancela o `setInterval` atual (se houver). Chamado em `ngOnDestroy`,
   * antes de reiniciar o timer e quando `avisos` cai para <= 1 item.
   */
  private pararTimer(): void {
    if (this.intervaloProgresso !== null) {
      clearInterval(this.intervaloProgresso);
      this.intervaloProgresso = null;
    }
  }

  /**
   * Tick do timer (a cada `TICK_MS`).
   *
   * - Quando pausado: NAO incrementa o progresso (barra congela).
   * - Caso contrario: avanca o progresso proporcionalmente. Ao atingir
   *   100% reseta para 0 e dispara `avancarPagina()`.
   *
   * Como o componente usa `ChangeDetectionStrategy.OnPush`, e necessario
   * chamar `markForCheck()` em cada tick para que a largura da barra
   * (binding `[style.width.%]="progresso"`) seja re-renderizada.
   */
  private tick(): void {
    if (this.pausado) {
      return;
    }
    const incremento = 100 / (INTERVALO_AUTOPLAY_MS / TICK_MS);
    this.progresso += incremento;
    if (this.progresso >= 100) {
      this.progresso = 0;
      this.avancarPagina();
    }
    this.cdr.markForCheck();
  }

  /**
   * Avanca para a proxima pagina do carrossel. Implementa loop manual:
   * ao ultrapassar a ultima pagina valida, volta para 0 (necessario porque
   * `[circular]="false"` no PrimeNG -- usar `circular=true` causou bugs
   * conhecidos com clones de itens).
   *
   * Reaproveita `calcularUltimaPagina()` para nao duplicar a logica de
   * calculo da ultima pagina (tambem usada no loop manual das setas).
   */
  private avancarPagina(): void {
    const total = this.avisosRotacionados.length;
    if (total <= 1) {
      return;
    }
    const ultimaPagina = this.calcularUltimaPagina();
    const proxima = this.paginaAtual + 1;
    this.paginaAtual = proxima > ultimaPagina ? 0 : proxima;
    this.cdr.markForCheck();
  }

  // -------------------------------------------------------------------------
  // Helpers de calculo de paginacao (compartilhados pelo autoplay e pelo
  // loop manual das setas).
  // -------------------------------------------------------------------------

  /**
   * Calcula o indice da ultima pagina valida do carrossel.
   *
   *   - `avisosRotacionados` tem `avisos.length + 2` itens (slot inicial
   *     e slot final em `null`).
   *   - O p-carousel mostra `numVisivel` itens por pagina (3 em desktop,
   *     1 em telas estreitas, espelhando o `responsiveOptions`).
   *   - Leitura sob demanda de `window.innerWidth` evita reintroduzir um
   *     listener de resize.
   *   - Ultima pagina valida = total - numVisivel (com floor em 0).
   */
  private calcularUltimaPagina(): number {
    const total = this.avisosRotacionados.length;
    const numVisivel = typeof window !== 'undefined' && window.innerWidth < BREAKPOINT_MOBILE_PX ? 1 : 3;
    return Math.max(0, total - numVisivel);
  }

  /**
   * Indica se o carrossel esta atualmente na ultima pagina valida.
   */
  private estaNaUltimaPagina(): boolean {
    return this.paginaAtual >= this.calcularUltimaPagina();
  }

  // -------------------------------------------------------------------------
  // Loop manual das setas do carrossel.
  //
  // Veja comentario de bloco proximo a declaracao de `observadorNavegadores`
  // para a estrategia completa. Resumo:
  //   1. Listener `click` em fase de captura no `.p-carousel`: detecta
  //      clique em `.p-carousel-next` na ultima pagina e em
  //      `.p-carousel-prev` na primeira, reposicionando manualmente.
  //   2. MutationObserver remove `disabled` / `.p-disabled` aplicados pelo
  //      PrimeNG nos botoes para que eles continuem clicaveis no limite.
  // -------------------------------------------------------------------------

  /**
   * Inicializa MutationObserver e listener de clique em capture phase
   * para suportar loop manual nas setas do carrossel.
   *
   * Chamado em `ngAfterViewInit()` (uma unica vez por instancia).
   */
  private configurarLoopManualNavegadores(): void {
    const host = this.elementRef.nativeElement;
    const carrossel = host.querySelector('.p-carousel') as HTMLElement | null;
    if (!carrossel) {
      return;
    }

    // 1. Listener em capture phase: roda ANTES do handler interno do
    //    PrimeNG. Se o clique veio de uma seta em pagina-limite, aplica
    //    o loop manual e bloqueia a propagacao para o PrimeNG nao
    //    processar (evitando estados inconsistentes).
    const onClick = (event: Event): void => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      const botaoNext = target.closest('.p-carousel-next') as HTMLElement | null;
      const botaoPrev = target.closest('.p-carousel-prev') as HTMLElement | null;

      if (botaoNext && this.estaNaUltimaPagina()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.paginaAtual = 0;
        // Reseta o progresso para o ciclo continuar limpo apos a
        // interacao manual.
        this.progresso = 0;
        this.cdr.markForCheck();
        this.agendarAtualizacaoDestaque();
        return;
      }

      if (botaoPrev && this.paginaAtual <= 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.paginaAtual = this.calcularUltimaPagina();
        this.progresso = 0;
        this.cdr.markForCheck();
        this.agendarAtualizacaoDestaque();
        return;
      }
    };

    carrossel.addEventListener('click', onClick, { capture: true });
    this.removerListenerClique = (): void => {
      carrossel.removeEventListener('click', onClick, { capture: true } as EventListenerOptions);
    };

    // 2. Observer: remove `disabled` / `.p-disabled` aplicados pelo
    //    PrimeNG nos botoes quando estamos no limite. Sem isso, o
    //    browser nao dispara o evento `click` no botao desabilitado e o
    //    listener acima nem chega a rodar.
    const reabilitarBotoes = (): void => {
      const botaoes = carrossel.querySelectorAll<HTMLButtonElement>(
        '.p-carousel-prev, .p-carousel-next'
      );
      botaoes.forEach((botao) => {
        if (botao.hasAttribute('disabled')) {
          botao.removeAttribute('disabled');
        }
        if (botao.classList.contains('p-disabled')) {
          botao.classList.remove('p-disabled');
        }
      });
    };

    // Aplica imediatamente para corrigir o estado inicial e depois vigia
    // mudancas na arvore do carrossel.
    reabilitarBotoes();

    this.observadorNavegadores = new MutationObserver(() => {
      reabilitarBotoes();
    });
    this.observadorNavegadores.observe(carrossel, {
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'class']
    });
  }
}
